import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, videos, formatViews } from '@/mocks/videos';

const SITE_ORIGIN = 'http://saitemold.com';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const category = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const categoryVideos = useMemo(
    () => videos.filter((v) => v.categorySlug === slug),
    [slug]
  );

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_ORIGIN}/category/${slug}`,
        url: `${SITE_ORIGIN}/category/${slug}`,
        name: category
          ? `${category.name} - ${t('siteName')}`
          : t('siteName'),
        description: category?.description || '',
        isPartOf: {
          '@id': `${SITE_ORIGIN}/#website`,
        },
        breadcrumb: {
          '@id': `${SITE_ORIGIN}/category/${slug}#breadcrumb`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE_ORIGIN}/category/${slug}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: t('breadcrumb.home'),
            item: `${SITE_ORIGIN}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: category?.name || '分类',
          },
        ],
      },
    ],
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4">
            {t('category.notFound')}
          </h1>
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            {t('notFound.backHome')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      {/* Category Header */}
      <section className="pt-24 pb-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav aria-label="面包屑导航" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-foreground-700 transition-colors">
                  {t('breadcrumb.home')}
                </Link>
              </li>
              <li>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </li>
              <li className="text-foreground-900 font-medium">{category.name}</li>
            </ol>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-2">
                {category.name}
              </h1>
              <p className="text-foreground-600 text-base md:text-lg">
                {category.description}
              </p>
            </div>
            <span className="text-sm text-foreground-500 whitespace-nowrap">
              {t('category.videoCount', { count: category.videoCount.toLocaleString() })}
            </span>
          </div>
        </div>
      </section>

      {/* Other Categories */}
      <section className="py-4 px-4 md:px-8 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <span className="text-sm text-foreground-500 whitespace-nowrap flex-shrink-0">
              {t('category.otherCategories')}
            </span>
            {categories
              .filter((c) => c.slug !== slug)
              .map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  className="text-sm px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap flex-shrink-0"
                >
                  {c.name}
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="py-8 md:py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {categoryVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {categoryVideos.map((video) => (
                <article key={video.id} className="group">
                  <Link to={`/video/${video.id}`} className="block">
                    <div className="relative aspect-video rounded-xl overflow-hidden mb-4 border border-background-200/70">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <span className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-play-fill text-xl text-primary-500" />
                        </span>
                      </div>
                    </div>
                    <h3 className="text-base md:text-lg font-medium text-foreground-900 leading-snug mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-user-line" />
                        </span>
                        {video.creator}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-eye-line" />
                        </span>
                        {formatViews(video.views)}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-heart-line" />
                        </span>
                        {formatViews(video.likes)}
                      </span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500 text-lg mb-4">
                {t('category.empty')}
              </p>
              <Link
                to="/"
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                {t('category.browseOther')}
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
